import React, { useState, useEffect } from 'react'
import { Text } from '../styles/Components'
import { mq } from '../helpers'
import Image from 'next/image'
import moment from 'moment-timezone'
import ArrowUp from '../../public/images/arrow-up-red.svg'
import ArrowUpBlack from '../../public/images/arrow-up-black.svg'
import ArrowDown from '../../public/images/arrow-down-red.svg'
import ArrowDownBlack from '../../public/images/arrow-down-black.svg'
import CalendarRightArrow from '../../public/images/calendar-right-arrow.svg'
import CalendarLeftArrow from '../../public/images/calendar-left-arrow.svg'
import Calendar from 'react-calendar'
import { upperFirst } from 'lodash'
import styled from '@emotion/styled'
import { gsColors } from '../styles/Variables'
import { useMarketContext } from './MarketContext'
import { DateTime } from 'luxon'

import 'react-calendar/dist/Calendar.css'

const CalendarContainer = styled.div`
  width: 100%;

  ${(props) => (props.maxWidth ? `max-width: ${props.maxWidth}px;` : '')}
  ${(props) => (props.disabled ? `opacity: 0.5;` : '')}


  .calendar-button {
    color: #000;
    font-family: Euclid;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.36px;

    background-color: transparent;
    border: none;
    border-bottom: 1px solid
      ${(props) => (props.error ? gsColors.sunsetOrange : '#000')};
    display: flex;
    width: 100%;
    height: 54px;
    padding: 12px 0px;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    ${(props) => (props.disabled ? `cursor: not-allowed;` : 'cursor: pointer;')}

    ${mq('max', '768px')} {
      p {
        font-size: 16px !important;
      }
    }

    .placeholder {
      color: ${gsColors.meredithGrey};
      ${(props) => (props.error ? `color: ${gsColors.sunsetOrange};` : '')}
    }
  }

  .calendar-extended {
    width: 100%;

    color: #000;
    font-family: Euclid;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.36px;

    .react-calendar__navigation {
      span {
        font-family: Euclid;
      }
    }

    .calendar-tile {
      box-sizing: border-box;
      border: 0.5px solid ${gsColors.lightTangerine};
      color: #000;
      font-family: Euclid;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
      letter-spacing: 0.36px;

      &.react-calendar__tile--now {
        color: #000;
        background-color: #fff;
      }

      &.react-calendar__tile--active {
        color: #fff;
        background-color: ${gsColors.sunsetOrange};
      }
    }
  }
`

/**
 *
 * @param {boolean} open calendar show/hide
 * @param {function} toggle toggle calendar show/hide
 * @param {ISO date} date selected date, ISO string
 * @param {function} onClick select date function
 * @param {string} timezone optional— if not present, will resort to selected market
 * @param {boolean} format optional— if true, will format as "Today, August 15 / Tuesday, August 15" vs "08/15/23"
 * @returns
 */
const CalendarDropdown = React.forwardRef(
  (
    {
      open,
      toggle,
      date,
      onClick,
      timezone,
      format,
      maxWidth,
      placeholder = 'Select a date',
      arrowBlack = false,
      error = false,
      isRange = false,
      disabled = false
    },
    ref,
  ) => {
    const { selectedMarket } = useMarketContext()

    const [formattedDate, setFormattedDate] = useState('')

    useEffect(() => {
      if (Array.isArray(date)) {
        return
      }

      const dateTime = DateTime.fromJSDate(date, 'MM/dd/yyyy', {
        zone: timezone || selectedMarket?.timezone,
      })

      const daysAway = dateTime
        .startOf('day')
        .diff(DateTime.local().startOf('day'), 'days').days

      setFormattedDate(
        daysAway < 2
          ? upperFirst(dateTime.toRelativeCalendar()) +
              dateTime.toFormat(', MMMM d')
          : dateTime.toFormat('EEEE, MMMM d'),
      )
    }, [date, selectedMarket?.timezone, timezone])

    const isValidDate = Array.isArray(date)
      ? !!(moment(date[0]).isValid() && moment(date[1]).isValid())
      : !!moment(date).isValid()

    const toggleCal = () => {
      if (disabled) {
        return
      }

      toggle()
    }

    return (
      <CalendarContainer disabled={disabled} maxWidth={maxWidth} error={error} ref={ref}>
        <button type="button" className="calendar-button" onClick={toggleCal}>
          <Text fontSize={18} spacing={0.36}>
            {isValidDate ? (
              format ? (
                formattedDate
              ) : Array.isArray(date) ? (
                `${moment(date[0]).format('MM/DD/YYYY')} - ${moment(date[1]).format('MM/DD/YYYY')}`
              ) : (
                moment(date).format('MM/DD/YYYY')
              )
            ) : (
              <span className="placeholder">{placeholder}</span>
            )}
          </Text>
          {open ? (
            <Image src={arrowBlack ? ArrowUpBlack : ArrowUp} alt="arrow-up" />
          ) : (
            <Image
              src={arrowBlack ? ArrowDownBlack : ArrowDown}
              alt="arrow-down"
            />
          )}
        </button>
        {open && (
          <Calendar
            value={date}
            className="calendar-extended"
            calendarType="gregory"
            tileClassName="calendar-tile"
            minDate={new Date()}
            minDetail="year"
            next2Label={null}
            prev2Label={null}
            nextLabel={<Image src={CalendarRightArrow} alt="right-arrow" />}
            prevLabel={<Image src={CalendarLeftArrow} alt="left-arrow" />}
            onChange={onClick}
            returnValue={isRange ? 'range' : 'start'}
            selectRange={isRange}
          />
        )}
      </CalendarContainer>
    )
  },
)

export default CalendarDropdown
